import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLoaderData, useFetcher } from '@remix-run/react';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { classNames } from '~/utils/classNames';
import { selectionStore, type AI_Model, type LLM_Provider } from '~/lib/persistence/selectionStore';
import FigmaOAuthModal from '~/components/chat/FigmaOAuthModal';
import CodeIntegration from '~/components/chat/CodeIntegration';
import { ProjectRulesModal } from '~/components/chat/ProjectRulesModal';
import { Dropdown, type DropdownOption } from '~/components/ui/Dropdown';
import { SupabaseOauthModal } from '~/components/supabase/SupabaseOAuthModal';
import { type Framework, setFramework, setLanguage, techStackSelectionStore } from '~/lib/stores/techStackSelection';
import type { AssistantMessageBuilder } from '~/utils/getTemplates';
import HelpMenu from '~/components/ui/HelpMenu';
import { isDev } from '~/utils/constants';
import type { FrontendData } from '~/types/figma-simplified-api';
import { examplePrompts, CATEGORIES, type ExamplePrompt } from '~/utils/examplePrompts';
import { TemplateDetailsModal } from '~/components/home/TemplateDetailsModal';
import iconMap from '~/components/ui/Icons/IconsMapping';
import { setDescriptionFromExamplePrompt, projectId } from '~/lib/persistence/useChatHistory';
import {
  setAllSupabaseData,
  supabaseConnectionStatusStore,
  updateSupabaseConnectionStatus,
  githubPlatformStore,
  apiSpecStore,
  hasApiSpec,
} from '~/lib/stores/integrations';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { chatState, setStatus } from '~/lib/stores/chatState';
import { NewChatInput } from '~/components/chat/NewChatInput.client';
import IconsMapping from '~/components/ui/Icons/IconsMapping';
import GithubModal from '~/components/chat/GithubModal';
import type { HomeLoaderData } from '~/routes/home';
import * as Sentry from '@sentry/remix';
import { useStorageStatus } from '~/hooks/useStorageStatus';
import { StorageExceededModal } from '~/components/modals/StorageExceededModal.client';
import { useFigmaAndAttachments } from '@/lib/hooks/useFigmaAndAttachments';
import type { AttachedImage } from '@/lib/stores/figmaAndImages';
import { useNavigate } from '@remix-run/react';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';
import NewPill from '~/components/ui/NewPill';
import WhatsNewModal, { WHATS_NEW_PILL_LABEL_KEY, hasSeenWhatsNew } from '~/components/modals/WhatsNewModal';
import FigmaIcon from '~/components/ui/Icons/FigmaIcon';
import { isOfferAvailable, useOfferTimer } from '~/components/ui/OfferTimer';
import DiscountedOfferModal from '~/components/ui/DiscountedOfferModal';
import { useRouteLoaderData } from '@remix-run/react';
import type { ServerLoaderData } from '~/root';
import { useFormbricksSurvey } from '~/components/survey/FormbricksSurvey';
import { isMobile } from '@/utils/mobile';

// Community projects data
// const communityProjects = [
//   {
//     id: '1',
//     titleKey: 'community.projects.project1.title',
//     descriptionKey: 'community.projects.project1.description',
//     titleDefault: 'Travel Platform Landing Page',
//     descriptionDefault: 'A beautiful landing page for a travel platform with modern design and smooth user experience.',
//     author: 'laddha.vedant1',
//     likes: 0,
//     isTrending: true,
//     icon: 'landingPageIcon',
//     demoUrl: 'https://orm-landing-page-58wu-dualite.netlify.app/',
//     imageUrl:
//       'https://framerusercontent.com/images/RWCCLKd5lacADc3FdkYCh3GSpPU.png?scale-down-to=1024&width=2940&height=1654',
//   },
//   {
//     id: '2',
//     titleKey: 'community.projects.project2.title',
//     descriptionKey: 'community.projects.project2.description',
//     titleDefault: 'SEO Hero Section',
//     descriptionDefault:
//       'A beautiful hero section for a SEO landing page with modern design and smooth user experience.',
//     author: 'dualiteindia',
//     likes: 0,
//     icon: 'pacManIcon',
//     demoUrl: 'https://seo-hero-section-q6hd-dualite.netlify.app/',
//     imageUrl: '/images/newTemp1.webp',
//   },
//   {
//     id: '3',
//     titleKey: 'community.projects.project3.title',
//     descriptionKey: 'community.projects.project3.description',
//     titleDefault: 'TEDx Event Registration Web App',
//     descriptionDefault:
//       'Join us for an extraordinary journey of inspiration, innovation, and ideas that have the power to change the world.',
//     author: 'mdabdulrahaman987',
//     likes: 0,
//     icon: 'landingPageIcon',
//     demoUrl: 'https://ion-web-app-4ju1-alphaproject.netlify.app/',
//     imageUrl:
//       'https://framerusercontent.com/images/DyBA3FE1YGUjCtsKH3jGo6jqnc.png?scale-down-to=1024&width=2940&height=1594',
//   },
//   {
//     id: '4',
//     titleKey: 'community.projects.project4.title',
//     descriptionKey: 'community.projects.project4.description',
//     titleDefault: 'Planilha de Valuation Empresarial',
//     descriptionDefault: 'Descodificando o poder de pensar diferente e criar o futuro.',
//     author: 'ibsonjunior',
//     likes: 0,
//     icon: 'flowChartIcon',
//     demoUrl: 'https://empresarial-z88y-alphaproject.netlify.app/',
//     imageUrl:
//       'https://framerusercontent.com/images/rjufiwHEjL21GwJE5aihfYEk.png?scale-down-to=1024&width=2940&height=1594',
//   },
//   {
//     id: '5',
//     titleKey: 'community.projects.project5.title',
//     descriptionKey: 'community.projects.project5.description',
//     titleDefault: 'Plataforma BelezaJá - SaaS para Salões e Barbearias',
//     descriptionDefault:
//       'Gestão de agenda, pagamentos automáticos, relatórios financeiros e muito mais. Tudo em uma plataforma simples e intuitiva para salões e barbearias.',
//     author: 'gustavo26111994',
//     likes: 0,
//     icon: 'pmaIcon',
//     demoUrl: 'https://barbearias-3dp2-alphaproject.netlify.app/',
//     imageUrl:
//       'https://framerusercontent.com/images/QtBSG2IHxXFoorqOYH7NSDIVM.png?scale-down-to=1024&width=2940&height=1594',
//   },
//   {
//     id: '6',
//     titleKey: 'community.projects.project6.title',
//     descriptionKey: 'community.projects.project6.description',
//     titleDefault: 'Guerreiro-FigJan-App',
//     descriptionDefault:
//       'Versão particular para ser compartilhada no meu site, para os usuários, rabiscarem suas ideias.',
//     author: 'roberto.carvalho.guerreiro',
//     likes: 0,
//     icon: 'notionIcon',
//     demoUrl: 'https://t-generator-0nx1-alphaproject.netlify.app/',
//     imageUrl:
//       'https://framerusercontent.com/images/AQuppvNdrtyz6g1RIzjJbBPHaI.png?scale-down-to=1024&width=2940&height=1594',
//   },
//   {
//     id: '7',
//     titleKey: 'community.projects.project7.title',
//     descriptionKey: 'community.projects.project7.description',
//     titleDefault: 'EquiMind - Mental Health Platform',
//     descriptionDefault:
//       'A safe, supportive platform designed specifically for young people in underserved communities.',
//     author: 'mandala.hemanth88',
//     likes: 0,
//     icon: 'mobileAppIcon',
//     demoUrl: 'https://th-platform-zpek-alphaproject.netlify.app/',
//     imageUrl:
//       'https://framerusercontent.com/images/cJ3oLzwwlaRckaY5Gn5sIXt64A.png?scale-down-to=1024&width=2940&height=1594',
//   },
// ];

export const llmProviderOptions: DropdownOption[] = [
  { value: 'gcp', label: 'Google (Vertex)' },
  { value: 'aws', label: 'AWS (Bedrock)' },
];

// Model tiers, labeled per provider — the underlying `value` (tier) is provider-agnostic,
// but which concrete model each tier resolves to depends on the provider (see getLLM in model.ts).
export const aiModelOptionsByProvider: Record<LLM_Provider, DropdownOption[]> = {
  gcp: [
    { value: 'dev-ai-model', label: 'Mini (Gemini 2.5)' },
    { value: 'dev-better', label: 'Standard (Gemini 3)' },
    { value: 'custom', label: 'Pro (GLM 5)' },
    { value: 'prod-ai-model', label: 'Ultra (Gemini 3.1 Pro)' },
    { value: 'dev-mock-ai-model', label: 'Dummy Model' },
  ],
  aws: [
    { value: 'dev-ai-model', label: 'Mini (Claude Haiku 4.5)' },
    { value: 'dev-better', label: 'Standard (Claude Haiku 4.5)' },
    { value: 'custom', label: 'Pro (Nova 2 Lite)' },
    { value: 'prod-ai-model', label: 'Ultra (Claude Sonnet 4.6)' },
    { value: 'dev-mock-ai-model', label: 'Dummy Model' },
  ],
};

export const getAiModelOptions = (provider: LLM_Provider): DropdownOption[] =>
  aiModelOptionsByProvider[provider] ?? aiModelOptionsByProvider.gcp;

// Examples and Community Section Component
interface ExamplesAndCommunitySectionProps {
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleTemplateClick?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    repoUrl: string,
    text: string,
    assistantMessageBuilder?: AssistantMessageBuilder,
    framework?: Framework,
  ) => void;
}

const ExamplesAndCommunitySection: React.FC<ExamplesAndCommunitySectionProps> = ({
  textareaRef,
  handleInputChange,
  handleTemplateClick,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<ExamplePrompt | null>(null);
  const { t } = useTranslation();
  const { trackTemplateEvent } = usePosthogCommonEvents();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredPrompts = examplePrompts.filter((prompt) => {
    if (selectedCategory === 'all') {
      return true;
    }

    return prompt.category === selectedCategory;
  });

  return (
    <div id="examples-community" className="bg-transparent w-full max-w-7xl mx-auto mt-16 sm:mt-6 sm:mt-8 px-4 sm:px-6">
      <div className="flex flex-col items-center gap-y-4 mb-8">
        {/* Main Heading */}
        <h1 className="text-xl sm:text-2xl font-bold text-dualite-text-primary mb-2 mt-5">{t('chat.getInspired')}</h1>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {CATEGORIES.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={classNames(
                'flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border justify-center',
                selectedCategory === category.id
                  ? 'bg-dualite-background-cta text-dualite-text-cta border-dualite-border-primary shadow-lg'
                  : 'bg-dualite-background-button text-dualite-text-tertiary border-dualite-border-primary hover:bg-dualite-background-buttonHover hover:text-dualite-text-secondary',
              )}
            >
              <div className={`${category.icon} text-2xl`} />
              <span className="w-full text-center">
                {t('chat.examplePrompts.categories.' + category.id, category.label)}
              </span>
            </button>
          ))}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full md:w-5/6 justify-items-center">
          {filteredPrompts.map((prompt, index) => (
            <motion.div
              key={prompt.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              onClick={(e) => {
                e.stopPropagation();
                trackTemplateEvent('clicked', {
                  template_id: prompt.id,
                  template_name: t(prompt.titleKey, prompt.titleDefault),
                  template_repo_url: prompt.repoUrl,
                  framework: prompt.framework,
                });
                setSelectedTemplate(prompt);
              }}
              className="group cursor-pointer w-full max-w-sm"
            >
              <div
                className={`relative h-full overflow-hidden rounded-xl transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5 ${
                  index === 0
                    ? 'bg-dualite-background-buttonHover border-2 border-dualite-border-primary hover:border-dualite-border-secondary'
                    : 'bg-dualite-background-button hover:bg-dualite-background-button-hover border border-dualite-border-primary hover:border-dualite-border-secondary'
                }`}
              >
                {/* Image Area */}
                <div className="relative w-full h-32 overflow-hidden flex items-center justify-center transition-colors duration-300">
                  {prompt.image ? (
                    <img
                      src={prompt.image}
                      alt={prompt.titleDefault}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <img
                        src="/images/alpha-logo.svg"
                        alt="Dualite"
                        className="w-20 opacity-50 group-hover:opacity-75 transition-opacity duration-300"
                      />
                    </div>
                  )}

                  {/* Popular badge for first template */}
                  {index === 0 && (
                    <div className="absolute top-2 left-2">
                      <div className="px-2 py-1 flex items-center gap-1 rounded-full bg-white text-black text-xs font-semibold shadow-lg">
                        <div className="i-ph:star-fill text-xs" />
                        Popular
                      </div>
                    </div>
                  )}

                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-dualite-background-button backdrop-blur-sm">
                      <div className="i-ph:arrow-right text-white text-xs" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3.5">
                  {/* Title */}
                  <h3 className="text-sm font-semibold text-dualite-text-primary mb-1.5 transition-colors duration-200 line-clamp-1">
                    {t(prompt.titleKey, prompt.titleDefault)}
                  </h3>

                  {/* Description */}
                  <p className="text-xs text-dualite-text-secondary group-hover:text-dualite-text-primary transition-colors duration-200 line-clamp-3 line-height-normal">
                    {(() => {
                      const translatedText = t(prompt.textKey, prompt.textDefault);
                      return translatedText.length > 200 ? `${translatedText.substring(0, 200)}...` : translatedText;
                    })()}
                  </p>

                  {/* Footer with framework */}
                  {/* <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <div className="i-ph:code text-xs" />
                      {FRAMEWORK_CONFIGS[prompt.framework]?.label || 'React + Tailwind'}
                    </span>
                  </div> */}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Template Details Modal */}
      <TemplateDetailsModal
        isOpen={!!selectedTemplate}
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        onRemix={(tmpl) => {
          setSelectedTemplate(null);

          const translatedTitle = t(tmpl.titleKey, tmpl.titleDefault);
          const translatedText = t(tmpl.textKey, tmpl.textDefault);

          setDescriptionFromExamplePrompt(translatedTitle);

          selectionStore.setKey('templateId', tmpl.id);
          selectionStore.setKey('templateName', translatedTitle);

          if (handleInputChange && textareaRef?.current) {
            textareaRef.current.value = translatedText;

            const event = {
              target: textareaRef.current,
              preventDefault: () => {
                /* empty */
              },
            } as unknown as React.ChangeEvent<HTMLTextAreaElement>;

            handleInputChange(event);
            handleTemplateClick?.(
              null as any,
              tmpl.repoUrl,
              translatedText,
              tmpl.assistantMessageBuilder,
              tmpl.framework,
            );
          }

          const introElement = document.getElementById('intro');

          if (introElement) {
            introElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            const scrollContainer =
              document.querySelector<HTMLElement>('.home-grey-box') ?? document.getElementById('main-scroll-container');

            if (scrollContainer) {
              scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        }}
      />
    </div>
  );
};

export const HomeView: React.FC = () => {
  const offerTimer = useOfferTimer();
  const rootData = useRouteLoaderData('root') as ServerLoaderData | undefined;
  const isFreeUser = rootData?.userData ? !rootData.userData.isSubscribed : false;

  // Survey trigger logic for free users with 2+ prompts
  const promptsUsed = parseInt(rootData?.userData?.promptsUsed || '0', 10);

  // Auto-open once per login session if user is authenticated and hasn't seen it
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(() => {
    if (typeof window !== 'undefined' && rootData?.user) {
      return !hasSeenWhatsNew();
    }

    return false;
  });

  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return isOfferAvailable() && offerTimer.isActive && isFreeUser;
    }

    return false;
  });

  // Automatically close the modal if the offer expires while it's open
  useEffect(() => {
    if (!offerTimer.isActive && isDiscountModalOpen) {
      setIsDiscountModalOpen(false);
    }
  }, [offerTimer.isActive, isDiscountModalOpen]);

  const { t, i18n } = useTranslation();

  const isBrazilOrPortuguese = rootData?.currency === 'BRL' || i18n.language?.startsWith('pt');
  const surveyTriggerId = isBrazilOrPortuguese ? 'two_plus_trigger_pt' : 'two_plus_trigger_en';
  const shouldShowSurvey = !isDev && isFreeUser && promptsUsed > 2;

  // Trigger formbricks survey for free users with 2+ prompts
  useFormbricksSurvey({
    triggerId: surveyTriggerId,
    userEmail: rootData?.user?.email,
    shouldTrigger: shouldShowSurvey,
  });

  useEffect(() => {
    if (!isDev) {
      selectionStore.setKey('aiModel', 'prod-ai-model');
    }
  }, []);

  const fetcher = useFetcher();
  const loaderData = useLoaderData<HomeLoaderData>();
  const token = loaderData.token ?? '';
  const navigate = useNavigate();

  const { trackActivatedUser, trackGithubEvent } = usePosthogCommonEvents();

  const paymentUrl = loaderData.VITE_PUBLIC_PAYMENT_URL;
  const githubCreds = {
    GITHUB_CLIENT_ID: loaderData.GITHUB_CLIENT_ID,
  };

  const figma = useFigmaAndAttachments();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle fetcher errors
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && (fetcher.data as any).error) {
      console.error('[HomeView] Error from action:', (fetcher.data as any).error);
      toast.error('Failed to submit message. Please try again.');
      setStatus('ready');
    }
  }, [fetcher.state, fetcher.data]);

  // Handle successful action response: persist payload to sessionStorage and navigate
  useEffect(() => {
    if (fetcher.state !== 'idle' || !fetcher.data) {
      return;
    }

    const data = fetcher.data as { projectId?: string; pendingMessage?: unknown };

    if (!data.projectId) {
      return;
    }

    try {
      if (data.pendingMessage) {
        window.sessionStorage.setItem('pendingMessage', JSON.stringify(data.pendingMessage));
      }
    } catch (error) {
      console.error('[HomeView] Failed to store pending message in sessionStorage', error);
    }

    projectId.set(data.projectId);
    navigate(`/chat/${data.projectId}`);
  }, [fetcher.state, fetcher.data, navigate]);

  const { status } = useStore(chatState);
  const selectedFramework = useStore(techStackSelectionStore).framework;
  const selectedLanguage = useStore(techStackSelectionStore).language;
  const packageManager = useStore(techStackSelectionStore).packageManager;
  const supabaseConnectionStatus = useStore(supabaseConnectionStatusStore);
  const { githubRepoUsagePercent, totalRepoUsagePercent, indexDBUsagePercent, indexDBUsageMB } = useStorageStatus();
  const selection = useStore(selectionStore);

  // Modal states
  const [showGithubModal, setShowGithubModal] = useState(false);
  const [supabaseModalIsOpen, setSupabaseModalIsOpen] = useState(false);
  const [isCodeIntegrationOpen, setIsCodeIntegrationOpen] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showManageStorageModal, setShowManageStorageModal] = useState(false);

  // Storage choice flow state (event-driven prompt → cloud/local decision)
  const [storageChoiceFlowActive, setStorageChoiceFlowActive] = useState(false);
  const pendingSubmissionRef = useRef<{
    event: React.UIEvent;
    messageInput: string;
    isExamplePromptParam?: boolean;
    githubRepoUrl?: string;
    assistantMessageBuilder?: AssistantMessageBuilder;
    attachedImagesOverride?: AttachedImage[] | null;
    extractedDesignOverride?: Partial<FrontendData> | null;
  } | null>(null);
  const bypassStorageChoiceRef = useRef(false);

  // Success states - showSuccessIcon indicates API spec is attached
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);

  // Use store for API spec - read for display purposes
  const openApiSpec = useStore(apiSpecStore);

  // GitHub processing states
  const [uploadingToWorkBench, setUploadingToWorkBench] = useState(false);
  const [processingGithubRepo, setProcessingGithubRepo] = useState(false);

  // Initialize supabase connection status
  useEffect(() => {
    updateSupabaseConnectionStatus();
  }, []);

  // Event-driven: listen for prompt-pre-submit to open storage choice modal
  useEffect(() => {
    const handleStorageChoiceRequired = () => {
      setStorageChoiceFlowActive(true);
      setSupabaseModalIsOpen(true);
    };

    window.addEventListener('dualite:prompt-pre-submit', handleStorageChoiceRequired);

    return () => window.removeEventListener('dualite:prompt-pre-submit', handleStorageChoiceRequired);
  }, []);

  const _executePendingSubmission = () => {
    const pending = pendingSubmissionRef.current;

    if (!pending) {
      return;
    }

    bypassStorageChoiceRef.current = true;
    handleSendMessage(
      pending.event,
      pending.messageInput,
      pending.isExamplePromptParam,
      pending.githubRepoUrl,
      pending.assistantMessageBuilder,
      pending.attachedImagesOverride,
      pending.extractedDesignOverride,
    );
    pendingSubmissionRef.current = null;
    setStorageChoiceFlowActive(false);
  };

  useEffect(() => {
    const promptFromLocalStorage = localStorage.getItem('prompt');

    if (promptFromLocalStorage) {
      const cleanedPrompt = promptFromLocalStorage.replace(/^"|"$/g, '');
      const examplePromptIds = examplePrompts.map((prompt) => prompt.id);

      if (examplePromptIds.includes(cleanedPrompt)) {
        const examplePrompt = examplePrompts.find((prompt) => prompt.id === cleanedPrompt);

        if (examplePrompt) {
          const translatedTitle = t(examplePrompt.titleKey, examplePrompt.titleDefault);
          const translatedText = t(examplePrompt.textKey, examplePrompt.textDefault);

          setDescriptionFromExamplePrompt(translatedTitle);

          if (textareaRef.current) {
            textareaRef.current.value = translatedText;
            setInput(translatedText);
          }

          setTimeout(() => {
            handleExamplePrompt(
              null as any,
              examplePrompt.repoUrl,
              translatedText,
              examplePrompt.assistantMessageBuilder,
              examplePrompt.framework,
            );
          }, 0);
        }
      } else {
        setInput(cleanedPrompt);

        if (textareaRef.current) {
          textareaRef.current.value = cleanedPrompt;
        }

        setTimeout(() => {
          handleSendMessage(null as any, cleanedPrompt);
        }, 0);
      }

      localStorage.removeItem('prompt');
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    if (event?.target?.value?.length <= 50000) {
      setInput(event.target.value);
    } else {
      alert('Input is too large. Please reduce the input size.');
    }
  };

  // previously sendMessage @Chat.client.tsx:1279
  const handleSendMessage = async (
    _event: React.UIEvent,
    messageInput?: string,
    isExamplePromptParam?: boolean,
    githubRepoUrl?: string,
    assistantMessageBuilder?: AssistantMessageBuilder,
    attachedImagesOverride?: AttachedImage[] | null,
    extractedDesignOverride?: Partial<FrontendData> | null,
  ) => {
    const _input = messageInput || input;

    if (_input?.length === 0 || status === 'streaming' || status === 'submitted') {
      return;
    }

    // this is storage exceeded error modal
    if (totalRepoUsagePercent >= 100 || indexDBUsagePercent >= 100) {
      setShowManageStorageModal(true);
      return;
    }

    setStatus('submitted');

    const isHtmlFramework = selectedFramework.includes('html');

    // Event-driven storage choice: if supabase not connected, ask user before submitting
    if (!isHtmlFramework && !bypassStorageChoiceRef.current && !supabaseConnectionStatus?.isConnected) {
      pendingSubmissionRef.current = {
        event: _event,
        messageInput: _input,
        isExamplePromptParam,
        githubRepoUrl,
        assistantMessageBuilder,
        attachedImagesOverride,
        extractedDesignOverride,
      };

      window.dispatchEvent(new CustomEvent('dualite:prompt-pre-submit'));
      setStatus('ready');

      return;
    }

    bypassStorageChoiceRef.current = false;

    // Get design/images from hook if not overridden
    const figmaData = figma.getDesignForPrompt();
    const currentAttachedImages = attachedImagesOverride !== undefined ? attachedImagesOverride : figma.attachedImages;
    const currentExtractedDesign = extractedDesignOverride !== undefined ? extractedDesignOverride : figmaData.design;
    const currentFigmaUrl = figmaData.figmaUrl;

    // Use Remix form submission instead of sessionStorage
    console.log('[HomeView] Preparing form submission with data:', {
      input: _input,
      isExamplePrompt: isExamplePromptParam,
      githubRepoUrl,
      hasAssistantMessageBuilder: !!assistantMessageBuilder,
      attachedImagesCount: currentAttachedImages?.length || 0,
      attachedImages: currentAttachedImages,

      hasExtractedDesign: !!currentExtractedDesign,
      figmaUrl: currentFigmaUrl,
      framework: techStackSelectionStore.get().framework,
      language: techStackSelectionStore.get().language,
    });

    const formData = new FormData();
    formData.append('intent', 'send-message');
    formData.append('input', _input);
    formData.append('isExamplePrompt', String(isExamplePromptParam || false));

    if (githubRepoUrl) {
      formData.append('githubRepoUrl', githubRepoUrl);
    }

    if (assistantMessageBuilder) {
      formData.append('assistantMessageBuilder', JSON.stringify(assistantMessageBuilder));
    }

    if (currentAttachedImages) {
      formData.append('attachedImages', JSON.stringify(currentAttachedImages));
    }

    if (currentExtractedDesign) {
      formData.append('extractedDesign', JSON.stringify(currentExtractedDesign));
    }

    if (currentFigmaUrl) {
      formData.append('figmaUrl', currentFigmaUrl);
    }

    if (openApiSpec) {
      formData.append('openApiSpec', openApiSpec);
    }

    const techStack = techStackSelectionStore.get();

    if (techStack.framework) {
      formData.append('framework', techStack.framework);
    }

    if (techStack.language) {
      formData.append('language', techStack.language);
    }

    // Submit the form using fetcher to avoid page reload
    console.log('[HomeView] Submitting form to /home action');
    console.log(
      '[HomeView] FormData entries:',
      Array.from(formData.entries()).map(([key, value]) => ({
        key,
        valueType: typeof value,
        valueLength: typeof value === 'string' ? value.length : 'N/A',
      })),
    );

    // Track Activation Event
    let activationChannel: 'chat' | 'figma' | 'github' | 'templates' = 'chat';

    if (githubRepoUrl) {
      if (isExamplePromptParam) {
        activationChannel = 'templates';
      } else {
        activationChannel = 'github';
      }
    } else if (currentFigmaUrl || currentExtractedDesign) {
      activationChannel = 'figma';
    }

    // Image source categorization for dashboard breakdown (prompt = ref images, figma = design/URL; no assets at project creation)
    const promptImageCount = currentAttachedImages?.length || 0;
    const hasFigmaImage = !!currentExtractedDesign || !!currentFigmaUrl;
    const imageSources: ('prompt' | 'asset' | 'figma')[] = [];

    if (promptImageCount > 0) {
      imageSources.push('prompt');
    }

    if (hasFigmaImage) {
      imageSources.push('figma');
    }

    trackActivatedUser(activationChannel, {
      has_images: promptImageCount > 0 || hasFigmaImage,
      image_count: promptImageCount + (hasFigmaImage ? 1 : 0),
      image_sources: imageSources,
      has_prompt_images: promptImageCount > 0,
      has_asset_images: false,
      has_figma_image: hasFigmaImage,
      prompt_image_count: promptImageCount,
      asset_image_count: 0,
      has_figma: hasFigmaImage,
      has_github: !!githubRepoUrl,
      is_example_prompt: !!isExamplePromptParam,
      framework: techStackSelectionStore.get().framework,
    });

    // Use fetcher to submit without page reload
    fetcher.submit(formData, {
      method: 'post',
      action: '/home',
    });
  };

  const handleExamplePrompt = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    repoUrl: string,
    text: string,
    assistantMessageBuilder?: AssistantMessageBuilder,
    framework: Framework = 'react_tailwind',
  ) => {
    setFramework(framework);

    // since all templates are in ts right now.
    // TODO-Prakhar : Make Language also dynamic
    setLanguage('ts');
    setUploadingToWorkBench(true);
    selectionStore.setKey('projectSource', 'alpha_template');

    const platformFromUrl = new URL(repoUrl).hostname.split('.')[0];
    githubPlatformStore.set(platformFromUrl);

    await handleSendMessage(e, text, true, repoUrl, assistantMessageBuilder);
    setUploadingToWorkBench(false);
    setProcessingGithubRepo(false);
  };

  const handleGithubRepoSubmit = async (repoUrl: string) => {
    setUploadingToWorkBench(true);

    const platformFromUrl = new URL(repoUrl).hostname.split('.')[0];
    githubPlatformStore.set(platformFromUrl);

    const githubPrompt = `Importing code from ${platformFromUrl}`;

    Sentry.captureMessage(
      `PRE_IMPORT [handleGithubRepoSubmit] FRAMEWORK: ${selectedFramework} LANGUAGE: ${selectedLanguage} PACKAGE MANAGER: ${packageManager} PLATFORM: ${platformFromUrl}`,
      'info',
    );

    // Track GitHub import
    try {
      trackGithubEvent('import_started', {
        repo_url: repoUrl,
        platform: platformFromUrl,
        framework: selectedFramework,
        language: selectedLanguage,
      });
    } catch (error) {
      console.error('[PostHog] Failed to track github_import_started:', error);
    }

    await handleSendMessage(null as any, githubPrompt, false, repoUrl);
    setShowGithubModal(false);
    setUploadingToWorkBench(false);
    setProcessingGithubRepo(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent, messageInput?: string) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        return;
      }

      event.preventDefault();

      if (figma.hasFigmaError) {
        toast.error('Please remove the invalid Figma design before sending');
        return;
      }

      handleSendMessage(event, messageInput);
    }
  };

  return (
    <div
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--dualite-scrollbar-thumb) transparent',
      }}
      className="relative flex flex-col h-fit w-full bg-transparent overflow-x-hidden sm:overflow-x-visible overflow-y-scroll"
    >
      <div className="flex w-full mt-5 h-full">
        <div className="-mt-4 flex flex-col grow min-w-(--chat-min-width) h-fit relative">
          <div className="flex flex-col justify-center min-h-[calc(100vh-264px)]">
            {/* Hero Section */}
            <div id="intro" className="flex flex-col items-center justify-center w-chat mx-auto px-4 sm:px-0">
              <div className="flex flex-col gap-5 items-center text-center mb-8 sm:mb-12 max-w-lg mt-16">
                <NewPill
                  onClick={
                    rootData?.user
                      ? () => setIsWhatsNewOpen(true)
                      : () => window.open('https://www.youtube.com/watch?v=-NP4Rl1gfJg', '_blank')
                  }
                  ctaText="Learn More"
                >
                  {t(WHATS_NEW_PILL_LABEL_KEY)}
                </NewPill>

                <h1
                  className="text-3xl sm:text-4xl lg:text-[50px] font-normal leading-[1.2] tracking-[0] text-center bg-clip-text text-transparent"
                  style={{
                    background: 'var(--dualite-text-hero-gradient)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    fontFamily: 'Instrument Serif',
                  }}
                >
                  What should we build today?
                </h1>
              </div>

              {isDev && (
                <div className=" mb-5 flex flex-col sm:flex-row gap-2">
                  <Dropdown
                    id="llmProvider-select"
                    options={llmProviderOptions}
                    value={selection.llmProvider}
                    onChange={(value) => {
                      selectionStore.setKey('llmProvider', value as LLM_Provider);
                    }}
                    className="min-w-[160px]"
                  />
                  <Dropdown
                    id="aiModel-select"
                    options={getAiModelOptions(selection.llmProvider)}
                    value={selection.aiModel}
                    onChange={(value) => {
                      selectionStore.setKey('aiModel', value as AI_Model);
                    }}
                    className="min-w-[200px]"
                  />
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="h-fit mx-auto z-prompt relative mt-24 sm:mt-0">
              <NewChatInput
                textareaRef={textareaRef}
                input={input}
                status={status}
                chatStarted={false}
                loading={loading}
                showSuccessIcon={showSuccessIcon}
                messages={[]}
                isBlocked={false}
                handleInputChange={handleInputChange}
                handleKeyDown={handleKeyDown}
                onSendMessage={handleSendMessage}
                onStop={() => {
                  /* empty */
                }}
                setIsCodeIntegrationOpen={setIsCodeIntegrationOpen}
                setShowRulesModal={setShowRulesModal}
                setShowSuccessIcon={setShowSuccessIcon}
                setMessages={() => {
                  /** */
                }}
              />
            </div>

            {/* Import Options */}
            <div className="flex flex-col gap-2 items-center justify-center mt-6 px-4 sm:px-0">
              <div className="flex gap-2">
                <button
                  disabled={status === 'streaming' || status === 'submitted' || loading}
                  onClick={() => figma.openModal()}
                  className={classNames(
                    'flex items-center gap-2 px-4 py-2 h-[35px] bg-dualite-background-button hover:bg-dualite-background-buttonHover rounded-xl transition-all duration-200 border border-dualite-border-primary hover:border-dualite-border-secondary hover:shadow-sm hover:-translate-y-0.5 sm:w-auto max-w-xs',
                  )}
                  title={t('chat.importFromFigma')}
                >
                  <FigmaIcon size={14} />
                  {isMobile() ? null : (
                    <span className="text-sm text-dualite-text-primary">{t('chat.importFromFigma')}</span>
                  )}
                </button>
                <button
                  onClick={() => setSupabaseModalIsOpen(true)}
                  disabled={status === 'streaming' || status === 'submitted' || loading}
                  className={classNames(
                    'flex items-center gap-2 px-4 py-2 h-[35px] bg-dualite-background-button hover:bg-dualite-background-buttonHover rounded-xl transition-all duration-200 border border-dualite-border-primary hover:border-dualite-border-secondary hover:shadow-sm hover:-translate-y-0.5 sm:w-auto max-w-xs',
                    supabaseConnectionStatus?.isConnected ? 'ring-2 ring-green-500' : '',
                  )}
                  title={t('chat.connectSupabase')}
                >
                  {IconsMapping.supabaseIcon}
                  {isMobile() ? null : (
                    <span className="text-sm text-dualite-text-primary">
                      {supabaseConnectionStatus?.isConnected
                        ? t('chat.connectedToSupabase')
                        : t('chat.importFromSupabase')}
                    </span>
                  )}
                  {supabaseConnectionStatus?.isConnected && <div className="i-ph:check text-green-500" />}
                </button>
                <button
                  onClick={() => setShowGithubModal(true)}
                  className="flex items-center gap-2 px-4 h-[35px] py-2 bg-dualite-background-button hover:bg-dualite-background-buttonHover rounded-xl transition-all duration-200 border border-dualite-border-primary hover:border-dualite-border-secondary hover:shadow-sm hover:-translate-y-0.5 sm:w-auto max-w-xs"
                >
                  <svg width="18" height="18" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6.14896 0C5.34147 0 4.54188 0.159048 3.79586 0.468062C3.04983 0.777076 2.37197 1.23001 1.80099 1.80099C0.647835 2.95414 0 4.51816 0 6.14896C0 8.8668 1.76475 11.1727 4.20589 11.9905C4.51334 12.0397 4.61172 11.849 4.61172 11.683V10.6439C2.90846 11.0128 2.54567 9.81989 2.54567 9.81989C2.26282 9.10661 1.86314 8.91599 1.86314 8.91599C1.30358 8.53476 1.90618 8.54706 1.90618 8.54706C2.52107 8.5901 2.84697 9.1804 2.84697 9.1804C3.38193 10.115 4.28583 9.83834 4.63632 9.69076C4.69166 9.29108 4.85153 9.02053 5.0237 8.8668C3.65863 8.71308 2.22592 8.18427 2.22592 5.84151C2.22592 5.15898 2.45958 4.61172 2.85927 4.17515C2.79778 4.02142 2.58256 3.38193 2.92076 2.55182C2.92076 2.55182 3.43727 2.3858 4.61172 3.17901C5.09749 3.04374 5.6263 2.9761 6.14896 2.9761C6.67162 2.9761 7.20043 3.04374 7.6862 3.17901C8.86065 2.3858 9.37717 2.55182 9.37717 2.55182C9.71536 3.38193 9.50015 4.02142 9.43866 4.17515C9.83834 4.61172 10.072 5.15898 10.072 5.84151C10.072 8.19042 8.63314 8.70693 7.26192 8.86065C7.48329 9.05127 7.6862 9.42636 7.6862 9.99821V11.683C7.6862 11.849 7.78459 12.0458 8.09818 11.9905C10.5393 11.1665 12.2979 8.8668 12.2979 6.14896C12.2979 5.34147 12.1389 4.54188 11.8299 3.79586C11.5208 3.04983 11.0679 2.37197 10.4969 1.80099C9.92595 1.23001 9.24809 0.777076 8.50207 0.468062C7.75604 0.159048 6.95645 0 6.14896 0Z"
                      fill="var(--dualite-text-primary)"
                    />
                  </svg>
                  {isMobile() ? null : (
                    <span className="text-sm text-dualite-text-primary">{t('chat.importFromGithub')}</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Examples and Community Section */}

          <ExamplesAndCommunitySection
            textareaRef={textareaRef}
            handleInputChange={handleInputChange}
            handleTemplateClick={handleExamplePrompt}
          />
        </div>

        {isCodeIntegrationOpen && (
          <CodeIntegration
            isOpen={isCodeIntegrationOpen}
            onClose={() => setIsCodeIntegrationOpen(false)}
            onSave={() => setShowSuccessIcon(true)}
            selectedFramework={selectedFramework}
          />
        )}
      </div>

      {/* Help Menu - hidden on mobile */}
      <div className="hidden md:block">
        <HelpMenu position="bottom-right" withBg={false} />
      </div>

      {/* Modals */}
      <FigmaOAuthModal />

      <ProjectRulesModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />

      {supabaseModalIsOpen && (
        <SupabaseOauthModal
          isOpen={supabaseModalIsOpen}
          onClose={() => {
            setSupabaseModalIsOpen(false);
            setStorageChoiceFlowActive(false);
            pendingSubmissionRef.current = null;
          }}
          initialStep={storageChoiceFlowActive ? 'storage-choice' : undefined}
          onLocalChoice={() => {
            setSupabaseModalIsOpen(false);
            _executePendingSubmission();
          }}
          onSuccess={(credentials) => {
            setAllSupabaseData(
              credentials.projectId,
              credentials.projectName,
              credentials.anonKey,
              credentials.organizationId,
              credentials.organizationName,
            );
            updateSupabaseConnectionStatus();
            toast.success('Successfully connected to Supabase project');
            setSupabaseModalIsOpen(false);

            if (storageChoiceFlowActive) {
              _executePendingSubmission();
            }
          }}
          onError={(error) => {
            toast.error('Failed to connect to Supabase: ' + error.message);
            setSupabaseModalIsOpen(false);
            setStorageChoiceFlowActive(false);
            pendingSubmissionRef.current = null;
          }}
          currentProjectId={undefined}
          currentProjectName={undefined}
          currentOrganizationId={undefined}
          currentOrganizationName={undefined}
        />
      )}

      {showGithubModal && (
        <GithubModal
          setProcessingGithubRepo={setProcessingGithubRepo}
          isOpen={showGithubModal}
          onClose={() => setShowGithubModal(false)}
          onSubmitRepo={handleGithubRepoSubmit}
          isUploadingToWb={uploadingToWorkBench}
          githubCreds={githubCreds}
          isChatStarted={false}
          isProcessingGithubRepo={processingGithubRepo}
          githubRepoUsagePercent={githubRepoUsagePercent}
          setShowManageStorageModal={setShowManageStorageModal}
          indexDBUsagePercent={indexDBUsagePercent}
          paymentUrl={paymentUrl}
        />
      )}

      {/* <DiscountedOfferModal
        isOpen={isDiscountModalOpen}
        onClose={() => setIsDiscountModalOpen(false)}
        paymentUrl={paymentUrl}
      /> */}

      {showManageStorageModal && (
        <StorageExceededModal
          isOpen={showManageStorageModal}
          onClose={() => setShowManageStorageModal(false)}
          setIsProUpgradeModalOpen={() => {
            /* empty */
          }}
        />
      )}

      <WhatsNewModal isOpen={isWhatsNewOpen} onClose={() => setIsWhatsNewOpen(false)} />
    </div>
  );
};
